import type { Editor, Range, SuggestionListRef } from 'OpalChat/TiptapEditorTypes';
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { Box, Flex, Separator, Text } from '@optiaxiom/react';
import {
  IconBracketsCurly,
  IconCalendar,
  IconFile,
  IconInputNumeric,
  IconInputText,
  IconPen,
  IconPlus,
  IconScrewdriverWrench,
  IconToggleOff,
} from '@optimizely/axiom-icons';

import type { AgentParameter, AgentParameterType } from '@frontend/types';

import type { SuggestionItem } from '../types';

import { useSuggestionKeyboard } from './hooks/useSuggestionKeyboard';
import { styles } from './AgentFormPromptTemplate.css';
import { SectionHeader, SuggestionItem as SuggestionItemComponent } from './Common';

const iconMap: Record<string, React.ReactNode> = {
  string: <IconInputText />,
  number: <IconInputNumeric />,
  boolean: <IconToggleOff />,
  datetime: <IconCalendar />,
  json: <IconBracketsCurly />,
  object: <IconBracketsCurly />,
  file: <IconFile />,
};

const ACTION_ITEMS = {
  'create-variable': {
    id: 'action:create-variable',
    label: 'Create variable',
    searchTerms: ['create variable'],
  },
  'add-tool': {
    id: 'action:add-tool',
    label: 'Add tool',
    searchTerms: ['add tool'],
  },
  'add-instructions': {
    id: 'action:add-instructions',
    label: 'Add instructions',
    searchTerms: ['add instructions'],
  },
  'add-retrieval': {
    id: 'action:add-retrieval',
    label: 'Add retrieval query',
    searchTerms: ['add retrieval query', 'rag'],
  },
  'add-retrieval-files': {
    id: 'action:add-retrieval-files',
    label: 'Add retrieval query with files',
    searchTerms: ['add retrieval query with files', 'rag'],
  },
} as const;

type ActionType = keyof typeof ACTION_ITEMS;

const shouldShowAction = (actionType: ActionType, lowerQuery: string): boolean => {
  if (!lowerQuery) return true;
  return ACTION_ITEMS[actionType].searchTerms.some(term => term.includes(lowerQuery));
};

const createActionItem = (actionType: ActionType): SuggestionItem => ({
  id: ACTION_ITEMS[actionType].id,
  label: ACTION_ITEMS[actionType].label,
  type: 'action',
  actionType,
});

export type AgentFormPromptTemplateProps = {
  editor: Editor;
  range: Range;
  command: (item: SuggestionItem) => void;
  query: string;
  variables: Array<AgentParameter<AgentParameterType>> | null;
  onCreateVariable: () => void;
  onEditVariable: (variable: AgentParameter<AgentParameterType>) => void;
  onInsertVariable?: (editor: Editor, variableName: string) => void;
  tools?: string[];
  onAddTool: () => void;
  onEditTool: (tool: string) => void;
  onInsertTool: (editor: Editor, toolName: string) => void;
  onInsertInstructions: (editor: Editor) => void;
  onInsertRetrieval: (editor: Editor) => void;
  onInsertRetrievalWithFiles: (editor: Editor) => void;
};

export const AgentFormPromptTemplate = forwardRef<SuggestionListRef, AgentFormPromptTemplateProps>(
  (
    {
      editor,
      range,
      command,
      query,
      variables,
      onCreateVariable,
      onEditVariable,
      onInsertVariable,
      tools,
      onAddTool,
      onEditTool,
      onInsertTool,
      onInsertInstructions,
      onInsertRetrieval,
      onInsertRetrievalWithFiles,
    },
    ref
  ) => {
    const lowerQuery = query?.toLowerCase() ?? '';

    // Compute filtered data for rendering
    const filteredVariables = useMemo(
      () => variables?.filter(v => v.name.toLowerCase().includes(lowerQuery)) ?? [],
      [variables, lowerQuery]
    );
    const filteredTools = useMemo(
      () => tools?.filter(t => t.toLowerCase().includes(lowerQuery)) ?? [],
      [tools, lowerQuery]
    );

    // Check which action items should be shown based on query
    const showCreateVariable = shouldShowAction('create-variable', lowerQuery);
    const showAddTool = shouldShowAction('add-tool', lowerQuery);
    const showAddInstructions = shouldShowAction('add-instructions', lowerQuery);
    const showAddRetrieval = shouldShowAction('add-retrieval', lowerQuery);
    const showAddRetrievalFiles = shouldShowAction('add-retrieval-files', lowerQuery);

    // Check if sections have content
    const hasVariablesSection = filteredVariables.length > 0 || showCreateVariable;
    const hasToolsSection = filteredTools.length > 0 || showAddTool;
    const hasInstructionsSection = showAddInstructions;
    const hasRetrievalSection = showAddRetrieval || showAddRetrievalFiles;

    const suggestionItems: SuggestionItem[] = useMemo(() => {
      const variableItems: SuggestionItem[] =
        variables
          ?.filter(variable => variable.name.toLowerCase().includes(lowerQuery))
          .map(variable => ({
            id: `variable:${variable.name}`,
            label: variable.name,
            type: 'variable' as const,
            data: variable,
          })) ?? [];

      const toolItems: SuggestionItem[] =
        tools
          ?.filter(tool => tool.toLowerCase().includes(lowerQuery))
          .map(tool => ({
            id: `tool:${tool}`,
            label: tool,
            type: 'tool' as const,
            data: tool,
          })) ?? [];

      return [
        ...variableItems,
        ...(showCreateVariable ? [createActionItem('create-variable')] : []),
        ...toolItems,
        ...(showAddTool ? [createActionItem('add-tool')] : []),
        ...(showAddInstructions ? [createActionItem('add-instructions')] : []),
        ...(showAddRetrieval ? [createActionItem('add-retrieval')] : []),
        ...(showAddRetrievalFiles ? [createActionItem('add-retrieval-files')] : []),
      ];
    }, [
      variables,
      tools,
      lowerQuery,
      showCreateVariable,
      showAddTool,
      showAddInstructions,
      showAddRetrieval,
      showAddRetrievalFiles,
    ]);

    const handleSelect = useCallback(
      (item: SuggestionItem) => {
        // Delete the slash command range first (removes "/" and any query text)
        editor.chain().focus().deleteRange(range).run();

        if (item.type === 'variable' && item.data && onInsertVariable) {
          const variable = item.data as AgentParameter<AgentParameterType>;
          onInsertVariable(editor, variable.name);
          command(item);
          return;
        }
        if (item.type === 'tool' && item.data && onInsertTool) {
          const tool = item.data as string;
          onInsertTool(editor, tool);
          command(item);
          return;
        }

        if (item.type === 'action') {
          switch (item.actionType) {
            case 'create-variable':
              onCreateVariable();
              command(item);
              break;
            case 'add-tool':
              onAddTool();
              command(item);
              break;
            case 'add-instructions':
              onInsertInstructions(editor);
              command(item);
              break;
            case 'add-retrieval':
              onInsertRetrieval(editor);
              command(item);
              break;
            case 'add-retrieval-files':
              onInsertRetrievalWithFiles(editor);
              command(item);
              break;
            default:
              break;
          }
        }
      },
      [
        editor,
        range,
        command,
        onInsertVariable,
        onInsertTool,
        onCreateVariable,
        onAddTool,
        onInsertInstructions,
        onInsertRetrieval,
        onInsertRetrievalWithFiles,
      ]
    );

    const handleEditItem = useCallback(
      (item: SuggestionItem) => {
        if (item.type === 'variable' && item.data) {
          onEditVariable(item.data as AgentParameter<AgentParameterType>);
        } else if (item.type === 'tool' && item.data) {
          onEditTool(item.data as string);
        }
      },
      [onEditVariable, onEditTool]
    );

    const { selectedId, isEditFocused, onKeyDown, handleItemClick } = useSuggestionKeyboard({
      items: suggestionItems,
      query,
      onSelect: handleSelect,
      onEdit: handleEditItem,
      hasEditAction: true,
    });

    useImperativeHandle(ref, () => ({ onKeyDown }), [onKeyDown]);

    // Check if there are no results to show
    const hasNoResults =
      !hasVariablesSection && !hasToolsSection && !hasInstructionsSection && !hasRetrievalSection;

    if (hasNoResults) {
      return (
        <Box
          flexDirection="column"
          rounded="md"
          shadow="md"
          border="1"
          borderColor="border.default"
          overflow="auto"
          bg="bg.default"
          p="6"
          className={styles.container}
          maxH="md"
        >
          <Text textAlign="center" color="fg.tertiary" fontSize="sm">
            No results found
          </Text>
        </Box>
      );
    }

    return (
      <Flex
        flexDirection="column"
        gap="6"
        rounded="md"
        shadow="md"
        border="1"
        borderColor="border.default"
        overflow="auto"
        bg="bg.default"
        pt="6"
        pb="12"
        className={styles.container}
      >
        {/* Variables Section */}
        {hasVariablesSection && (
          <>
            <SectionHeader title="Variables" />
            {filteredVariables.map(variable => {
              const itemId = `variable:${variable.name}`;
              const isItemSelected = selectedId === itemId;
              return (
                <SuggestionItemComponent
                  key={variable.name}
                  iconBefore={iconMap[variable.type] || <IconInputText />}
                  label={variable.name}
                  iconAfter={<IconPen />}
                  isSelected={isItemSelected}
                  isEditFocused={isItemSelected && isEditFocused}
                  onClick={() => handleItemClick(itemId)}
                  onEditClick={() => onEditVariable?.(variable)}
                />
              );
            })}
            {showCreateVariable && (
              <SuggestionItemComponent
                iconBefore={<IconPlus />}
                label={ACTION_ITEMS['create-variable'].label}
                isAction
                isSelected={selectedId === ACTION_ITEMS['create-variable'].id}
                onClick={() => handleItemClick(ACTION_ITEMS['create-variable'].id)}
              />
            )}
          </>
        )}

        {/* Tools Section */}
        {hasToolsSection && (
          <>
            {hasVariablesSection && <Separator />}
            <SectionHeader title="Tools" />
            {filteredTools.map(tool => {
              const itemId = `tool:${tool}`;
              const isItemSelected = selectedId === itemId;
              return (
                <SuggestionItemComponent
                  key={tool}
                  iconBefore={<IconScrewdriverWrench />}
                  label={tool}
                  iconAfter={<IconPen />}
                  isSelected={isItemSelected}
                  isEditFocused={isItemSelected && isEditFocused}
                  onClick={() => handleItemClick(itemId)}
                  onEditClick={() => onEditTool?.(tool)}
                />
              );
            })}
            {showAddTool && (
              <SuggestionItemComponent
                iconBefore={<IconPlus />}
                label={ACTION_ITEMS['add-tool'].label}
                isAction
                isSelected={selectedId === ACTION_ITEMS['add-tool'].id}
                onClick={() => handleItemClick(ACTION_ITEMS['add-tool'].id)}
              />
            )}
          </>
        )}

        {/* Instructions Section */}
        {hasInstructionsSection && (
          <>
            {(hasVariablesSection || hasToolsSection) && <Separator />}
            <SectionHeader title="Instructions" />
            <SuggestionItemComponent
              iconBefore={<IconPlus />}
              label={ACTION_ITEMS['add-instructions'].label}
              isAction
              isSelected={selectedId === ACTION_ITEMS['add-instructions'].id}
              onClick={() => handleItemClick(ACTION_ITEMS['add-instructions'].id)}
            />
          </>
        )}

        {/* Retrieval Section */}
        {hasRetrievalSection && (
          <>
            {(hasVariablesSection || hasToolsSection || hasInstructionsSection) && <Separator />}
            <SectionHeader title="Retrieval (RAG)" />
            {showAddRetrieval && (
              <SuggestionItemComponent
                iconBefore={<IconBracketsCurly />}
                label="Retrieve text"
                isAction
                isSelected={selectedId === ACTION_ITEMS['add-retrieval'].id}
                onClick={() => handleItemClick(ACTION_ITEMS['add-retrieval'].id)}
              />
            )}
            {showAddRetrievalFiles && (
              <SuggestionItemComponent
                iconBefore={<IconBracketsCurly />}
                label="Retrieve text and files"
                isAction
                isSelected={selectedId === ACTION_ITEMS['add-retrieval-files'].id}
                onClick={() => handleItemClick(ACTION_ITEMS['add-retrieval-files'].id)}
              />
            )}
          </>
        )}
      </Flex>
    );
  }
);

AgentFormPromptTemplate.displayName = 'AgentFormPromptTemplate';
